/**
 * Invitation Tracking Table Component
 *
 * Reusable table for displaying and managing invitation tracking data.
 * Pre-filters by event type (USI or HMC) and provides search, filter,
 * sort, pagination, and bulk operations.
 *
 * Features:
 * - Full integration with useInvitationTrackingStore
 * - Analytics summary cards (open rate, click rate, bounce rate, RSVP rate)
 * - Search input (filters by recipient email/name)
 * - Filter dropdowns for invitation type and status
 * - Status filter chips for quick toggle
 * - Sortable column headers (sent_at, recipient_email, status)
 * - RSVP status column with color-coded indicators
 * - Checkbox row selection with bulk action bar
 * - Row click to open InvitationDetailModal
 * - Loading skeleton, empty, and error states
 * - Pagination footer with prev/next
 *
 * Used by:
 * - USI Events Invitation Tracking page
 * - HMC Events Invitation Tracking page
 */

import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Search,
  ChevronUp,
  ChevronDown,
  ChevronsUpDown,
  ChevronLeft,
  ChevronRight,
  RefreshCw,
  Send,
  Bell,
  AlertCircle,
  AlertTriangle,
  Loader2,
  Mail,
  X,
  Eye,
  MousePointer,
  UserCheck,
  Check,
  HelpCircle,
  Minus,
  Download,
} from 'lucide-react';
import { Button, Checkbox, Input, EmptyState } from '@shared/components/ui';
import { useInvitationTrackingStore } from '@shared/store/invitationTrackingStore';
import {
  InvitationStatusBadge,
  InvitationTypeBadge,
  ALL_STATUSES,
  ALL_TYPES,
  getStatusLabel,
  getTypeLabel,
} from '@shared/components/InvitationStatusBadge';
import { InvitationDetailModal } from '@shared/components/InvitationDetailModal';
import { ReminderScheduleModal } from '@shared/components/ReminderScheduleModal';
import { BulkResendConfirmModal } from '@shared/components/BulkResendConfirmModal';
import { invitationTrackingApi } from '@shared/api/invitationTrackingApi';
import { toast } from '@shared/utils/notifications';
import type {
  EventType,
  InvitationStatus,
  InvitationType,
  InvitationRecord,
  RSVPStatus,
} from '@shared/types/invitationTracking';

// ============================================================
// PROPS
// ============================================================

interface InvitationTrackingTableProps {
  /** Pre-filter invitations to this event type */
  eventType: EventType;
}

// ============================================================
// DATE FORMATTING
// ============================================================

function formatDate(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' });
}

function formatDateTime(dateString: string | null): string {
  if (!dateString) return '-';
  const date = new Date(dateString);
  return date.toLocaleString([], {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/**
 * Truncate a string to a max length, appending "..." if truncated.
 */
function truncate(str: string | null, maxLen: number): string {
  if (!str) return '-';
  return str.length > maxLen ? str.slice(0, maxLen) + '...' : str;
}

// ============================================================
// ANALYTICS CARD SUB-COMPONENT
// ============================================================

function AnalyticsCard({ label, value, icon: Icon, color }: {
  label: string;
  value: string;
  icon: React.FC<{ className?: string }>;
  color: string;
}) {
  const colorClasses: Record<string, string> = {
    emerald: 'text-emerald-400',
    teal: 'text-teal-400',
    red: 'text-red-400',
    brand: 'text-brand-400',
  };
  const iconColor = colorClasses[color] || 'text-dark-muted';

  return (
    <div className="p-3 rounded-lg bg-dark-card border border-dark-border/30">
      <div className="flex items-center gap-2 mb-1">
        <Icon className={`w-4 h-4 ${iconColor}`} />
        <span className="text-xs text-dark-muted">{label}</span>
      </div>
      <span className="text-lg font-bold text-dark-text">{value}</span>
    </div>
  );
}

// ============================================================
// RSVP BADGE SUB-COMPONENT
// ============================================================

function RSVPBadge({ status }: { status?: RSVPStatus }) {
  if (!status) {
    return (
      <span className="inline-flex items-center gap-1 text-dark-muted">
        <Minus className="w-3.5 h-3.5" />
        <span className="text-xs">-</span>
      </span>
    );
  }

  if (status === 'yes') {
    return (
      <span className="inline-flex items-center gap-1 text-green-400">
        <Check className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">Yes</span>
      </span>
    );
  }

  if (status === 'no') {
    return (
      <span className="inline-flex items-center gap-1 text-red-400">
        <X className="w-3.5 h-3.5" />
        <span className="text-xs font-medium">No</span>
      </span>
    );
  }

  // maybe
  return (
    <span className="inline-flex items-center gap-1 text-yellow-400">
      <HelpCircle className="w-3.5 h-3.5" />
      <span className="text-xs font-medium">Maybe</span>
    </span>
  );
}

// ============================================================
// SORT ICON HELPER
// ============================================================

type SortField = 'sent_at' | 'recipient_email' | 'status';

function SortIcon({ field, currentSortBy, currentOrder }: {
  field: SortField;
  currentSortBy: string | undefined;
  currentOrder: 'asc' | 'desc' | undefined;
}) {
  if (currentSortBy !== field) {
    return <ChevronsUpDown className="w-3 h-3 text-dark-muted" />;
  }
  return currentOrder === 'asc'
    ? <ChevronUp className="w-3 h-3 text-brand-400" />
    : <ChevronDown className="w-3 h-3 text-brand-400" />;
}

// ============================================================
// SKELETON ROWS
// ============================================================

function SkeletonRow() {
  return (
    <tr className="animate-pulse">
      <td className="px-3 py-3"><div className="w-4 h-4 bg-dark-surface rounded" /></td>
      <td className="px-3 py-3">
        <div className="space-y-1">
          <div className="h-3 w-32 bg-dark-surface rounded" />
          <div className="h-2.5 w-44 bg-dark-surface rounded" />
        </div>
      </td>
      <td className="px-3 py-3 hidden md:table-cell"><div className="h-3 w-24 bg-dark-surface rounded" /></td>
      <td className="px-3 py-3"><div className="h-5 w-16 bg-dark-surface rounded-full" /></td>
      <td className="px-3 py-3 text-center"><div className="h-5 w-14 bg-dark-surface rounded-full mx-auto" /></td>
      <td className="px-3 py-3 text-center hidden md:table-cell"><div className="h-5 w-12 bg-dark-surface rounded mx-auto" /></td>
      <td className="px-3 py-3 hidden sm:table-cell"><div className="h-3 w-20 bg-dark-surface rounded" /></td>
      <td className="px-3 py-3 hidden lg:table-cell"><div className="h-3 w-36 bg-dark-surface rounded" /></td>
    </tr>
  );
}

// ============================================================
// MAIN COMPONENT
// ============================================================

export function InvitationTrackingTable({ eventType }: InvitationTrackingTableProps) {
  // Store state and actions
  const {
    invitations,
    total,
    stats,
    analytics,
    filters,
    isLoading,
    error,
    selectedIds,
    fetchInvitations,
    fetchStats,
    fetchAnalytics,
    updateFilter,
    resetFilters,
    setPage,
    toggleSelection,
    selectAll,
    clearSelection,
    bulkResend,
    bulkRemind,
  } = useInvitationTrackingStore();

  // Detail modal state
  const [detailInvitationId, setDetailInvitationId] = useState<number | null>(null);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);

  // Reminder modal state
  const [isReminderModalOpen, setIsReminderModalOpen] = useState(false);

  // Resend confirm modal state
  const [isResendModalOpen, setIsResendModalOpen] = useState(false);

  // ============================================
  // Mount: Set event_type filter and fetch data
  // ============================================
  useEffect(() => {
    updateFilter({ event_type: eventType });
    fetchStats(eventType);
    fetchAnalytics(eventType);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventType]);

  // ============================================
  // Derived values
  // ============================================
  const currentPage = filters.page ?? 1;
  const perPage = filters.per_page ?? 25;
  const totalPages = Math.ceil(total / perPage);
  const startIndex = (currentPage - 1) * perPage + 1;
  const endIndex = Math.min(currentPage * perPage, total);

  const allOnPageSelected = invitations.length > 0 && invitations.every((inv) => selectedIds.has(inv.id));
  const someOnPageSelected = invitations.some((inv) => selectedIds.has(inv.id));
  const selectionCount = selectedIds.size;

  // ============================================
  // Handlers
  // ============================================

  /** Handle search input with debounce built into updateFilter */
  const handleSearchChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      updateFilter({ search: e.target.value || undefined });
    },
    [updateFilter]
  );

  /** Toggle sort on a column */
  const handleSort = useCallback(
    (field: SortField) => {
      const isSameField = filters.sort_by === field;
      const nextOrder = isSameField && filters.sort_order === 'desc' ? 'asc' : 'desc';
      updateFilter({ sort_by: field, sort_order: nextOrder });
    },
    [filters.sort_by, filters.sort_order, updateFilter]
  );

  /** Handle filter by invitation type */
  const handleTypeFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as InvitationType | '';
      updateFilter({ invitation_type: value || undefined });
    },
    [updateFilter]
  );

  /** Handle filter by status */
  const handleStatusFilter = useCallback(
    (e: React.ChangeEvent<HTMLSelectElement>) => {
      const value = e.target.value as InvitationStatus | '';
      updateFilter({ status: value || undefined });
    },
    [updateFilter]
  );

  /** Quick-toggle a status filter chip */
  const handleStatusChipToggle = useCallback(
    (status: InvitationStatus) => {
      if (filters.status === status) {
        updateFilter({ status: undefined });
      } else {
        updateFilter({ status });
      }
    },
    [filters.status, updateFilter]
  );

  /** Header checkbox: toggle all on page */
  const handleHeaderCheckbox = useCallback(
    (checked: boolean) => {
      if (checked) {
        selectAll();
      } else {
        clearSelection();
      }
    },
    [selectAll, clearSelection]
  );

  /** Row click handler - opens detail modal */
  const handleRowClick = useCallback(
    (invitationId: number) => {
      setDetailInvitationId(invitationId);
      setIsDetailModalOpen(true);
    },
    []
  );

  /** Close detail modal */
  const handleDetailModalClose = useCallback(() => {
    setIsDetailModalOpen(false);
    setDetailInvitationId(null);
  }, []);

  /** Open bulk resend confirmation modal */
  const handleBulkResend = useCallback(() => {
    if (selectionCount === 0) return;
    setIsResendModalOpen(true);
  }, [selectionCount]);

  /** Resend confirm callback (called from modal) */
  const handleResendConfirm = useCallback(async () => {
    const result = await bulkResend(Array.from(selectedIds));
    toast.success(`Resent ${result.sent} invitation(s)`);
    if (result.failed > 0) {
      toast.error(`${result.failed} failed: ${result.errors.join(', ')}`);
    }
  }, [selectedIds, bulkResend]);

  /** Open bulk remind modal */
  const handleBulkRemind = useCallback(() => {
    if (selectionCount === 0) return;
    setIsReminderModalOpen(true);
  }, [selectionCount]);

  /** Reminder success callback (called from ReminderScheduleModal) */
  const handleReminderSuccess = useCallback(() => {
    clearSelection();
    fetchInvitations();
    fetchStats(eventType);
    fetchAnalytics(eventType);
  }, [clearSelection, fetchInvitations, fetchStats, fetchAnalytics, eventType]);

  /** Export handler */
  const handleExport = useCallback(async () => {
    try {
      const blob = await invitationTrackingApi.exportInvitations(filters);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invitation-tracking-${eventType}-${new Date().toISOString().split('T')[0]}.csv`;
      a.click();
      URL.revokeObjectURL(url);
      toast.success('Export started');
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Export failed';
      toast.error(message);
      console.error('Export failed:', err);
    }
  }, [filters, eventType]);

  /** Refresh handler */
  const handleRefresh = useCallback(() => {
    fetchInvitations();
    fetchStats(eventType);
    fetchAnalytics(eventType);
  }, [fetchInvitations, fetchStats, fetchAnalytics, eventType]);

  // ============================================
  // Stats chips - only show if stats are loaded
  // ============================================
  const statusChips = useMemo(() => {
    if (!stats) return null;

    return ALL_STATUSES
      .filter((s) => (stats.by_status?.[s] ?? 0) > 0)
      .map((status) => {
        const count = stats.by_status?.[status] ?? 0;
        const isActive = filters.status === status;
        return (
          <button
            key={status}
            onClick={() => handleStatusChipToggle(status)}
            className={`
              inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium
              transition-all duration-150 border
              ${isActive
                ? 'border-brand-500/50 bg-brand-500/20 text-brand-400'
                : 'border-dark-border/30 bg-dark-surface/50 text-dark-muted hover:text-dark-text hover:border-dark-border/50'
              }
            `}
          >
            {getStatusLabel(status)}
            <span className="text-[10px] opacity-75">({count})</span>
            {isActive && <X className="w-3 h-3" />}
          </button>
        );
      });
  }, [stats, filters.status, handleStatusChipToggle]);

  // ============================================
  // RENDER
  // ============================================

  return (
    <div className="space-y-3">
      {/* ====================== Analytics Cards ====================== */}
      {analytics && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <AnalyticsCard
            label="Open Rate"
            value={`${analytics.open_rate?.toFixed(1) || 0}%`}
            icon={Eye}
            color="emerald"
          />
          <AnalyticsCard
            label="Click Rate"
            value={`${analytics.click_rate?.toFixed(1) || 0}%`}
            icon={MousePointer}
            color="teal"
          />
          <AnalyticsCard
            label="Bounce Rate"
            value={`${analytics.bounce_rate?.toFixed(1) || 0}%`}
            icon={AlertTriangle}
            color="red"
          />
          <AnalyticsCard
            label="RSVP Rate"
            value={`${analytics.rsvp_rate?.toFixed(1) || 0}%`}
            icon={UserCheck}
            color="brand"
          />
        </div>
      )}

      {/* ====================== Stats Bar ====================== */}
      {stats && (
        <div className="flex items-center gap-3 md:gap-6 text-sm overflow-x-auto pb-1">
          <span className="text-dark-muted whitespace-nowrap">
            <Mail className="w-4 h-4 inline mr-1" />
            {stats.total} total
          </span>
          {ALL_TYPES.map((type) => {
            const count = stats.by_invitation_type?.[type] ?? 0;
            if (count === 0) return null;
            return (
              <span key={type} className="whitespace-nowrap">
                <InvitationTypeBadge type={type} />
                <span className="ml-1 text-dark-muted text-xs">({count})</span>
              </span>
            );
          })}
        </div>
      )}

      {/* ====================== Toolbar ====================== */}
      <div className="flex flex-wrap items-center gap-2 md:gap-3">
        {/* Search */}
        <div className="relative flex-1 min-w-[180px] max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-dark-muted pointer-events-none" />
          <input
            type="text"
            placeholder="Search recipient..."
            value={filters.search ?? ''}
            onChange={handleSearchChange}
            className="w-full pl-9 pr-3 py-2 bg-dark-surface border border-dark-border/30 rounded-lg text-sm text-dark-text placeholder:text-dark-muted focus:outline-none focus:border-brand-500 transition-colors"
          />
        </div>

        {/* Type filter */}
        <select
          value={filters.invitation_type ?? ''}
          onChange={handleTypeFilter}
          className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-lg text-sm text-dark-text focus:outline-none focus:border-brand-500 transition-colors"
        >
          <option value="">All Types</option>
          {ALL_TYPES.map((type) => (
            <option key={type} value={type}>{getTypeLabel(type)}</option>
          ))}
        </select>

        {/* Status filter */}
        <select
          value={filters.status ?? ''}
          onChange={handleStatusFilter}
          className="px-3 py-2 bg-dark-surface border border-dark-border/30 rounded-lg text-sm text-dark-text focus:outline-none focus:border-brand-500 transition-colors"
        >
          <option value="">All Statuses</option>
          {ALL_STATUSES.map((status) => (
            <option key={status} value={status}>{getStatusLabel(status)}</option>
          ))}
        </select>

        {/* Export */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleExport}
          disabled={isLoading}
          aria-label="Export invitations as CSV"
        >
          <Download className="w-4 h-4" />
        </Button>

        {/* Refresh */}
        <Button
          variant="ghost"
          size="sm"
          onClick={handleRefresh}
          disabled={isLoading}
          aria-label="Refresh invitations"
        >
          <RefreshCw className={`w-4 h-4 ${isLoading ? 'animate-spin' : ''}`} />
        </Button>
      </div>

      {/* ====================== Status Chips ====================== */}
      {statusChips && (
        <div className="flex items-center gap-2 flex-wrap">
          {statusChips}
        </div>
      )}

      {/* ====================== Bulk Action Bar ====================== */}
      {selectionCount > 0 && (
        <div className="flex items-center gap-3 px-4 py-2.5 bg-brand-500/10 border border-brand-500/30 rounded-lg">
          <span className="text-sm font-medium text-brand-400">
            {selectionCount} selected
          </span>
          <div className="flex items-center gap-2 ml-auto">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBulkResend}
              disabled={isLoading}
            >
              <Send className="w-4 h-4 mr-1.5" />
              Resend
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleBulkRemind}
              disabled={isLoading}
            >
              <Bell className="w-4 h-4 mr-1.5" />
              Remind
            </Button>
            <Button
              variant="ghost"
              size="xs"
              onClick={clearSelection}
              className="text-dark-muted"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>
        </div>
      )}

      {/* ====================== Error State ====================== */}
      {error && (
        <div className="flex items-center justify-center py-12">
          <div className="text-center space-y-4">
            <AlertCircle className="w-12 h-12 text-red-400 mx-auto" />
            <p className="text-dark-muted">{error}</p>
            <Button variant="secondary" size="sm" onClick={handleRefresh}>
              Try Again
            </Button>
          </div>
        </div>
      )}

      {/* ====================== Table ====================== */}
      {!error && (
        <div className="bg-dark-card rounded-lg border border-dark-border/30 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-dark-surface text-dark-muted uppercase text-xs">
                <tr>
                  {/* Checkbox */}
                  <th className="px-3 py-3 w-10">
                    <Checkbox
                      checked={allOnPageSelected}
                      indeterminate={someOnPageSelected && !allOnPageSelected}
                      onChange={handleHeaderCheckbox}
                      size={16}
                      aria-label="Select all invitations on this page"
                    />
                  </th>

                  {/* Recipient */}
                  <th className="px-3 py-3 text-left">
                    <button
                      onClick={() => handleSort('recipient_email')}
                      className="inline-flex items-center gap-1 hover:text-dark-text transition-colors"
                    >
                      Recipient
                      <SortIcon field="recipient_email" currentSortBy={filters.sort_by} currentOrder={filters.sort_order} />
                    </button>
                  </th>

                  {/* Organization */}
                  <th className="px-3 py-3 text-left hidden md:table-cell">Organization</th>

                  {/* Type */}
                  <th className="px-3 py-3 text-left">Type</th>

                  {/* Status */}
                  <th className="px-3 py-3 text-center">
                    <button
                      onClick={() => handleSort('status')}
                      className="inline-flex items-center gap-1 hover:text-dark-text transition-colors"
                    >
                      Status
                      <SortIcon field="status" currentSortBy={filters.sort_by} currentOrder={filters.sort_order} />
                    </button>
                  </th>

                  {/* RSVP - hidden on mobile */}
                  <th className="px-3 py-3 text-center hidden md:table-cell">RSVP</th>

                  {/* Sent Date */}
                  <th className="px-3 py-3 text-left hidden sm:table-cell">
                    <button
                      onClick={() => handleSort('sent_at')}
                      className="inline-flex items-center gap-1 hover:text-dark-text transition-colors"
                    >
                      Sent
                      <SortIcon field="sent_at" currentSortBy={filters.sort_by} currentOrder={filters.sort_order} />
                    </button>
                  </th>

                  {/* Subject */}
                  <th className="px-3 py-3 text-left hidden lg:table-cell">Subject</th>
                </tr>
              </thead>

              <tbody className="divide-y divide-dark-border/30">
                {/* Loading skeleton */}
                {isLoading && invitations.length === 0 && (
                  <>
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                    <SkeletonRow />
                  </>
                )}

                {/* Data rows */}
                {!isLoading && invitations.length === 0 ? null : (
                  invitations.map((inv) => (
                    <InvitationRow
                      key={inv.id}
                      invitation={inv}
                      isSelected={selectedIds.has(inv.id)}
                      onToggleSelect={() => toggleSelection(inv.id)}
                      onRowClick={() => handleRowClick(inv.id)}
                    />
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Empty state (shown inside card when not loading) */}
          {!isLoading && invitations.length === 0 && !error && (
            <EmptyState
              icon={Mail}
              title="No invitations found"
              description={
                filters.search || filters.status || filters.invitation_type
                  ? 'No invitations match your current filters. Try adjusting your search or clearing filters.'
                  : 'No invitation records yet for this event type.'
              }
              action={
                (filters.search || filters.status || filters.invitation_type)
                  ? { label: 'Clear Filters', onClick: resetFilters }
                  : undefined
              }
            />
          )}

          {/* Pagination footer */}
          {total > 0 && (
            <div className="flex items-center justify-between px-4 py-3 border-t border-dark-border/30 text-sm">
              <span className="text-dark-muted">
                Showing {startIndex}-{endIndex} of {total}
              </span>

              <div className="flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setPage(currentPage - 1)}
                  disabled={currentPage <= 1 || isLoading}
                  aria-label="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                  Prev
                </Button>

                <span className="text-dark-muted text-xs px-2">
                  Page {currentPage} of {totalPages}
                </span>

                <Button
                  variant="ghost"
                  size="xs"
                  onClick={() => setPage(currentPage + 1)}
                  disabled={currentPage >= totalPages || isLoading}
                  aria-label="Next page"
                >
                  Next
                  <ChevronRight className="w-4 h-4" />
                </Button>
              </div>
            </div>
          )}

          {/* Loading overlay for subsequent fetches (when data already exists) */}
          {isLoading && invitations.length > 0 && (
            <div className="flex items-center justify-center py-2 border-t border-dark-border/30">
              <Loader2 className="w-4 h-4 animate-spin text-brand-500 mr-2" />
              <span className="text-xs text-dark-muted">Updating...</span>
            </div>
          )}
        </div>
      )}

      {/* ====================== Detail Modal ====================== */}
      <InvitationDetailModal
        invitationId={detailInvitationId}
        isOpen={isDetailModalOpen}
        onClose={handleDetailModalClose}
      />

      {/* ====================== Reminder Schedule Modal ====================== */}
      <ReminderScheduleModal
        isOpen={isReminderModalOpen}
        onClose={() => setIsReminderModalOpen(false)}
        selectedIds={Array.from(selectedIds)}
        eventType={eventType}
        onSuccess={handleReminderSuccess}
      />

      {/* ====================== Bulk Resend Confirm Modal ====================== */}
      <BulkResendConfirmModal
        isOpen={isResendModalOpen}
        onClose={() => setIsResendModalOpen(false)}
        selectedIds={Array.from(selectedIds)}
        onConfirm={handleResendConfirm}
      />
    </div>
  );
}

// ============================================================
// INVITATION ROW (extracted for performance)
// ============================================================

interface InvitationRowProps {
  invitation: InvitationRecord;
  isSelected: boolean;
  onToggleSelect: () => void;
  onRowClick: () => void;
}

const InvitationRow = React.memo(function InvitationRow({
  invitation,
  isSelected,
  onToggleSelect,
  onRowClick,
}: InvitationRowProps) {
  /** Handle row click - don't trigger if clicking checkbox */
  const handleClick = useCallback(
    (e: React.MouseEvent) => {
      // Don't open modal when clicking on checkbox
      const target = e.target as HTMLElement;
      if (target.closest('[data-checkbox]') || target.tagName === 'INPUT') {
        return;
      }
      onRowClick();
    },
    [onRowClick]
  );

  return (
    <tr
      className={`hover:bg-dark-surface/50 transition-all duration-150 cursor-pointer ${isSelected ? 'bg-brand-500/5' : ''}`}
      onClick={handleClick}
    >
      {/* Checkbox */}
      <td className="px-3 py-3" data-checkbox>
        <Checkbox
          checked={isSelected}
          onChange={onToggleSelect}
          size={16}
          aria-label={`Select invitation for ${invitation.recipient_email}`}
        />
      </td>

      {/* Recipient (name + email) */}
      <td className="px-3 py-3">
        <div className="min-w-0">
          {invitation.recipient_name && (
            <p className="font-medium text-dark-text truncate max-w-[200px] lg:max-w-[280px]">
              {invitation.recipient_name}
            </p>
          )}
          <p className={`text-dark-muted truncate max-w-[200px] lg:max-w-[280px] ${!invitation.recipient_name ? 'text-dark-text' : 'text-xs'}`}>
            {invitation.recipient_email}
          </p>
        </div>
      </td>

      {/* Organization */}
      <td className="px-3 py-3 hidden md:table-cell">
        <span className="text-dark-muted truncate block max-w-[150px]">
          {invitation.recipient_org || '-'}
        </span>
      </td>

      {/* Type */}
      <td className="px-3 py-3">
        <InvitationTypeBadge type={invitation.invitation_type} />
      </td>

      {/* Status */}
      <td className="px-3 py-3 text-center">
        <InvitationStatusBadge status={invitation.status} />
      </td>

      {/* RSVP - hidden on mobile */}
      <td className="px-3 py-3 text-center hidden md:table-cell">
        <RSVPBadge status={invitation.rsvp_status} />
      </td>

      {/* Sent Date */}
      <td className="px-3 py-3 hidden sm:table-cell text-dark-muted whitespace-nowrap">
        {formatDateTime(invitation.sent_at)}
      </td>

      {/* Subject */}
      <td className="px-3 py-3 hidden lg:table-cell">
        <span className="text-dark-muted truncate block max-w-[200px]" title={invitation.subject || undefined}>
          {truncate(invitation.subject, 40)}
        </span>
      </td>
    </tr>
  );
});

export default InvitationTrackingTable;
